import { FileTypeLabelEnum, PageTypeEnum, TextMessage } from '@/constant';
import { useSearchParams } from '@/hooks';
import {
	ApiResponseEnum,
	TDocumentUploadDefineItem,
	TMappingDocumentItem,
} from '@/services';
import { CommonActions, useAppSelector } from '@/stores';
import {
	DefineDocumentUploadActions,
	DefineDocumentUploadSelectors,
	TDefineDocumentUploadListStore,
} from '@/stores/define-document-upload';
import { randomId, showAlert } from '@/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useFormik } from 'formik';
import { isNilOrEmpty } from 'ramda-adjunct';
import { AnyObject } from 'yup';

import {
	getColumns,
	mappingInit,
	StatusDocumentUpload,
	validationSchema,
} from './define-document-upload.config';
import {
	formatReqUpdateDocumentList,
	mergeArrayDocumentUpload,
} from './define-document-upload.helper';
import { SearchParamsDefineDocumentUpload } from './define-document-upload.type';
import { ExpandableDocumentWrapperRef } from './expandable-document-wrapper';
import { useDocumentUpload, useUploadFileLogic } from './hooks';

export const useLogic = () => {
	const expandableDocumentWrapperRef =
		useRef<ExpandableDocumentWrapperRef>(null);

	const listAPIUpload = useAppSelector(
		DefineDocumentUploadSelectors.selectDefineDocumentUploadList
	);

	const listAPIUploadFull = useAppSelector(
		DefineDocumentUploadSelectors.selectDefineDocumentUploadListFull
	);

	const fetching = useAppSelector(DefineDocumentUploadSelectors.selectFetching);

	const [searchParams] = useSearchParams<SearchParamsDefineDocumentUpload>();

	const { _refTableId, _refTableName } =
		searchParams as SearchParamsDefineDocumentUpload;

	const dispatch = useDispatch();

	const [listDocumentChange, setListDocumentChange] =
		useState<TDefineDocumentUploadListStore>([]);

	const mergeListDocument = useMemo(
		() => mergeArrayDocumentUpload(listAPIUpload, listDocumentChange),
		[listAPIUpload, listDocumentChange]
	);

	const listDocumentChangeFilterHidden = useMemo(
		() => mergeListDocument.filter((item) => !item.hidden),
		[mergeListDocument]
	);

	useEffect(() => {
		dispatch(CommonActions.getAutocompleteSuggestionRequest());

		return () => {
			dispatch(DefineDocumentUploadActions.clearDefineDocumentUploadList());
		};
	}, []);

	const formik = useFormik({
		initialValues: { documents: mergeListDocument },
		enableReinitialize: true,
		validationSchema,
		onSubmit: async (values) => {
			try {
				const reqUpdateDocumentList = formatReqUpdateDocumentList(
					values.documents
				);
				dispatch(
					DefineDocumentUploadActions.updateDocumentRequest({
						reqUpdateDocumentList,
						refTableId: Number(_refTableId),
						refTableName: _refTableName,
						callback: () => {
							window.parent.postMessage(ApiResponseEnum.SUCCESS);
						},
					})
				);
			} catch (error) {
				console.error('ERROR:', error);
			}
		},
	});

	const addNewDocument = useCallback(
		(record: AnyObject) => {
			if (record.isGroupHeader) {
				let templateDocument = listAPIUpload.find(
					(doc) => doc.documentType === record.documentType
				);

				if (!templateDocument) {
					templateDocument = mergeListDocument.find(
						(doc) => doc.documentType === record.documentType
					);
				}

				if (templateDocument) {
					const newDocument = {
						...templateDocument,
						id: randomId(),
						fileList: {} as TDocumentUploadDefineItem,
						fileName: '',
						mappingList: [mappingInit()] as TMappingDocumentItem[],
						typeName: '-',
						statusDocumentUpload: StatusDocumentUpload.ADD,
						modified: false,
						registDt: '',
					} as TDefineDocumentUploadListStore[0];

					setListDocumentChange((prev) => [...prev, newDocument]);
				}
				return;
			}
		},
		[mergeListDocument, listAPIUpload]
	);

	const updateDocumentChange = (
		record: AnyObject,
		newDocument: TDefineDocumentUploadListStore[0]
	) => {
		const index = listDocumentChange.findIndex((doc) => doc.id === record.id);

		if (index !== -1) {
			const updatedDocuments = listDocumentChange.map((doc, i) =>
				i === index ? newDocument : doc
			);
			setListDocumentChange(updatedDocuments);
		} else {
			setListDocumentChange((prev) => [...prev, newDocument]);
		}
	};

	const dataUploadFile = useUploadFileLogic();

	const addRowRelation = useCallback(
		(record: AnyObject, index?: number) => {
			const mappingList = [...(record.mappingList || [])];
			const newMapping = mappingInit();

			if (typeof index === 'number') {
				mappingList.splice(index + 2, 0, newMapping);
			} else {
				mappingList.push(newMapping);
			}

			const newDocument = {
				...(record as TDefineDocumentUploadListStore[0]),
				mappingList,
			};

			updateDocumentChange(record, newDocument);
		},
		[updateDocumentChange]
	);

	const subtractRelation = useCallback(
		(id: number, record: AnyObject) => {
			const newMappingList = (
				record as TDefineDocumentUploadListStore[0]
			).mappingList?.filter((item) => item.id !== id);

			const newDocument = {
				...(record as TDefineDocumentUploadListStore[0]),
				mappingList: newMappingList,
			};

			updateDocumentChange(record, newDocument);
		},
		[updateDocumentChange]
	);

	const onChangeRelation = useCallback(
		(value: string, record: AnyObject, id: number) => {
			const newMappingList = (
				record as TDefineDocumentUploadListStore[0]
			).mappingList?.map((item) =>
				item.id === id
					? {
							...item,
							refTableName: value as PageTypeEnum,
							refTableId: null as unknown as number,
						}
					: item
			);

			const newDocument = {
				...(record as TDefineDocumentUploadListStore[0]),
				mappingList: newMappingList,
			};

			updateDocumentChange(record, newDocument);
		},
		[updateDocumentChange]
	);

	const onChangeValue = useCallback(
		(value: string, record: AnyObject, id: number) => {
			const newMappingList = (
				record as TDefineDocumentUploadListStore[0]
			).mappingList?.map((item) =>
				item.id === id ? { ...item, refTableId: Number(value) } : item
			);

			const newDocument = {
				...(record as TDefineDocumentUploadListStore[0]),
				mappingList: newMappingList,
			};

			updateDocumentChange(record, newDocument);
		},
		[updateDocumentChange]
	);

	const updateDocumentList = (
		newDocumentDelete: TDefineDocumentUploadListStore[0]
	) => {
		const index = listDocumentChange.findIndex(
			(doc) => doc.id === newDocumentDelete.id
		);
		if (index !== -1) {
			return listDocumentChange.map((doc, i) =>
				i === index ? newDocumentDelete : doc
			);
		}

		return [...listDocumentChange, newDocumentDelete];
	};

	const createNewDocument = (
		deleteDocument: TDefineDocumentUploadListStore[0],
		keepMapping = true
	) => {
		return {
			...deleteDocument,
			mappingList: keepMapping ? deleteDocument.mappingList : [mappingInit()],
			id: randomId(),
			fileList: {} as TDocumentUploadDefineItem,
			fileName: '',
			typeName: '-',
			statusDocumentUpload: StatusDocumentUpload.ADD,
			hidden: false,
			modified: false,
			registDt: '',
		};
	};

	const createHiddenDocument = (
		document: TDefineDocumentUploadListStore[0],
		keepMapping = true
	) => {
		return {
			...document,
			mappingList: keepMapping
				? document.mappingList?.filter(
						(item) =>
							item.refTableName !== document.fileList.refTableName ||
							item.refTableId !== document.fileList.refTableId
					)
				: [mappingInit()],
			hidden: true,
		};
	};

	const handleAddNewDocument = (
		hiddenDocument: TDefineDocumentUploadListStore[0],
		keepMapping = true
	) => {
		const updatedDocuments = updateDocumentList(hiddenDocument);
		const newDocument = createNewDocument(hiddenDocument, keepMapping);

		const hiddenDocumentIndex = updatedDocuments.findIndex(
			(doc) => doc.id === hiddenDocument.id
		);
		updatedDocuments.splice(hiddenDocumentIndex + 1, 0, newDocument);

		return updatedDocuments;
	};

	const onDeleteDocument = useCallback(
		(id: number) => {
			const documentToDelete = mergeListDocument.find((doc) => doc.id === id);
			if (!documentToDelete) return;

			const hiddenDocument = createHiddenDocument(documentToDelete);
			const updatedDocuments = handleAddNewDocument(hiddenDocument);
			setListDocumentChange(updatedDocuments);

			const defineDocumentUploadListFull = listAPIUploadFull.map((doc) =>
				doc.id === id
					? { ...doc, statusDocumentUpload: StatusDocumentUpload.ADD }
					: doc
			);

			dispatch(
				DefineDocumentUploadActions.setDefineDocumentUploadListFull({
					defineDocumentUploadList: defineDocumentUploadListFull,
				})
			);
		},
		[mergeListDocument, listAPIUploadFull]
	);

	const onDeleteRow = useCallback(
		(record: AnyObject) => {
			const documentToDelete = record as TDefineDocumentUploadListStore[0];
			const hasManyDocumentType =
				listDocumentChangeFilterHidden.filter(
					(doc) => doc.documentType === documentToDelete.documentType
				).length > 1;

			if (hasManyDocumentType) {
				const hiddenDocument = createHiddenDocument(documentToDelete);
				updateDocumentChange(documentToDelete, hiddenDocument);
			} else {
				const document = mergeListDocument.find(
					(doc) => doc.id === documentToDelete.id
				);
				if (!document) return;

				const hiddenDocument = createHiddenDocument(document);
				const updatedDocuments = handleAddNewDocument(hiddenDocument, false);
				setListDocumentChange(updatedDocuments);
			}
		},
		[
			updateDocumentChange,
			listDocumentChangeFilterHidden,
			mergeListDocument,
			createHiddenDocument,
			handleAddNewDocument,
		]
	);

	const { onUploaded } = useDocumentUpload({
		mergeListDocument,
		createHiddenDocument,
		updateDocumentList,
		updateDocumentChange,
		setListDocumentChange,
	});

	const onSelectDocument = useCallback(
		(record: AnyObject) => {
			if (dataUploadFile.fileRowSelection.selectedRowKeys.length > 1) {
				showAlert(TextMessage.ALERT_68);
				return;
			}

			const newDocument = {
				...(record as TDefineDocumentUploadListStore[0]),
				fileName: dataUploadFile.fileRowSelection.selectedRows?.[0]?.name,
				fileList: {
					...(dataUploadFile.fileRowSelection
						.selectedRows?.[0] as TDocumentUploadDefineItem),
				},
				typeName: FileTypeLabelEnum.UPLOAD,
			};
			updateDocumentChange(record, newDocument);
			dataUploadFile.setLocalFile((prev) =>
				prev.filter(
					(file) =>
						file.id !== dataUploadFile.fileRowSelection.selectedRows?.[0]?.id
				)
			);
			dataUploadFile.fileRowSelection?.onReset?.();

			const indexCurrent = listAPIUploadFull.findIndex(
				(doc) => doc.id === newDocument.id
			);

			let listAPIUploadFullNew = [...listAPIUploadFull];
			if (indexCurrent !== -1) {
				listAPIUploadFullNew = listAPIUploadFullNew.map((doc) =>
					doc.id === newDocument.id ? newDocument : doc
				);
			} else {
				listAPIUploadFullNew = [...listAPIUploadFullNew, newDocument];
			}

			dispatch(
				DefineDocumentUploadActions.setDefineDocumentUploadListFull({
					defineDocumentUploadList: listAPIUploadFullNew,
				})
			);
		},
		[dataUploadFile.fileRowSelection, listAPIUploadFull]
	);

	const onSave = useCallback(async () => {
		const { validateForm, values, handleSubmit } = formik;
		const errors = await validateForm(values);
		handleSubmit();

		if (isNilOrEmpty(errors)) {
			return;
		}
		showAlert(TextMessage.ALERT_24);
	}, [formik]);

	const columns = useMemo(
		() =>
			getColumns({
				onDeleteDocument,
				onDeleteRow,
				addRowRelation,
				subtractRelation,
				onChangeRelation,
				onChangeValue,
				onUploaded,
				onSelectDocument,
				refTableId: Number(_refTableId),
				selectedRowKeys: dataUploadFile.fileRowSelection.selectedRowKeys,
				dataSource: listDocumentChangeFilterHidden,
			}),
		[
			onDeleteDocument,
			onDeleteRow,
			addRowRelation,
			subtractRelation,
			onChangeRelation,
			onChangeValue,
			_refTableId,
			dataUploadFile.fileRowSelection.selectedRowKeys,
			listDocumentChangeFilterHidden,
		]
	);

	return {
		expandableDocumentWrapperRef,
		onSave,
		columns,
		fetching,
		listDocumentChangeFilterHidden,
		formik,

		_refTableId,
		_refTableName,

		...dataUploadFile,
		addNewDocument,
	};
};
